/**
 * 工时录入弹窗组件
 * 支持3种分配模式：单日/总计/每天
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Space,
  Message,
  Avatar,
  Typography,
} from '@arco-design/web-react';
import dayjs, { Dayjs } from 'dayjs';
import { batchCreateWorkHours, updateWorkHour } from '@/api/work-hours';
import type { WorkHourCreateInput, WorkHour, WorkHourUpdateInput } from '@/types/work-hour';
import { getWorkdaysByDateRange } from '@/api/workdays';
import { createLogger } from '@/utils/logger';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// 创建模块级别的 logger
const logger = createLogger('WorkHourModal');

// 计算工作日提示信息
function formatWorkdayInfo(
  workdayCount: number,
  hours: number,
  mode: 'single' | 'total' | 'daily'
): string {
  if (workdayCount === 0) return '选择的日期范围内没有工作日';
  if (hours === 0) return `${workdayCount}个工作日`;

  if (mode === 'total') {
    const avgHours = (hours / workdayCount).toFixed(1);
    if (hours / workdayCount > 24) {
      return `⚠️ ${workdayCount}个工作日，日均 ${avgHours} 小时（超过24小时限制，无法保存）`;
    }
    return `${workdayCount}个工作日，日均 ${avgHours} 小时`;
  }

  if (mode === 'daily') {
    const totalHours = hours * workdayCount;
    return `${workdayCount}个工作日，共 ${totalHours.toFixed(1)} 小时`;
  }

  return `${workdayCount}个工作日`;
}

interface WorkHourModalProps {
  visible: boolean;
  taskId: string;
  taskName: string;
  assigneeId: string | null;
  assigneeName: string | null;
  hoursType: 'planned' | 'actual'; // 计划工时 or 实际工时
  editWorkHour?: WorkHour | null; // 编辑时传入的工时记录
  onClose: () => void;
  onSuccess: () => void;
}

type DateInput = Dayjs | Date | string | number;

interface WorkHourFormValues {
  dateRange: [DateInput, DateInput];
  hours: number;
  description?: string;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : typeof error === 'object' && error !== null && 'name' in error && (error as { name?: string }).name === 'AbortError';
}

function isDateRangeValue(value: unknown): value is [DateInput, DateInput] {
  return Array.isArray(value) && value.length === 2 && Boolean(value[0]) && Boolean(value[1]);
}

export default function WorkHourModal({
  visible,
  taskId,
  taskName,
  assigneeId,
  assigneeName,
  hoursType,
  editWorkHour,
  onClose,
  onSuccess,
}: WorkHourModalProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'single' | 'total' | 'daily'>('single');
  const [workdayInfo, setWorkdayInfo] = useState<string>('');
  const [isSingleDay, setIsSingleDay] = useState(true);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 编辑模式：初始化表单数据
  useEffect(() => {
    isMountedRef.current = true;

    if (visible) {
      if (editWorkHour) {
        // 编辑模式：设置表单值
        const workDate = dayjs(editWorkHour.workDate);
        const hours = hoursType === 'planned' ? editWorkHour.plannedHours : editWorkHour.actualHours;

        form.setFieldsValue({
          dateRange: [workDate, workDate], // 编辑模式只支持单日
          hours: hours,
          description: editWorkHour.description || '',
        });

        setMode('single');
        setIsSingleDay(true);
        setWorkdayInfo('');
      } else {
        // 创建模式：清空表单
        form.resetFields();
        setMode('single');
        setIsSingleDay(true);
        setWorkdayInfo('');
      }
    }

    return () => {
      isMountedRef.current = false;
      // 组件卸载时中止所有进行中的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [visible, editWorkHour, form, hoursType]);

  // 查询工作日并更新提示信息
  async function fetchAndUpdateWorkdayInfo(
    start: DateInput,
    end: DateInput,
    hours: number,
    currentMode: 'single' | 'total' | 'daily'
  ): Promise<void> {
    // 中止之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setWorkdayInfo('查询工作日中...');

      const startDate = dayjs(start).format('YYYY-MM-DD');
      const endDate = dayjs(end).format('YYYY-MM-DD');
      const workdayData = await getWorkdaysByDateRange(startDate, endDate);

      if (controller.signal.aborted) {
        logger.debug('请求已被中止，忽略结果');
        return;
      }

      const workdayCount = workdayData.filter(d => d.isWorkday).length;
      logger.debug('API 返回工作日', { workdayCount });

      setWorkdayInfo(formatWorkdayInfo(workdayCount, hours, currentMode));
    } catch (error: unknown) {
      if (isAbortError(error)) {
        logger.debug('请求被中止');
        return;
      }
      logger.error('查询工作日失败', error);
      setWorkdayInfo('⚠️ 查询工作日失败，请稍后重试');
    }
  }

  // 监听日期范围变化
  async function handleDateChange(_dateString: string[], dates: Dayjs[]) {
    logger.debug('handleDateChange', { dates });

    if (!dates || !dates[0] || !dates[1]) {
      setWorkdayInfo('');
      setIsSingleDay(true);
      setMode('single');
      return;
    }

    const [start, end] = dates;
    const daysDiff = dayjs(end).diff(dayjs(start), 'day') + 1;

    if (daysDiff === 1) {
      setMode('single');
      setIsSingleDay(true);
      setWorkdayInfo('');
    } else {
      setIsSingleDay(false);
      setMode('total');
      const hours = form.getFieldValue('hours') || 0;
      await fetchAndUpdateWorkdayInfo(start, end, hours, 'total');
    }
  }

  // 监听工时数变化
  async function handleHoursChange(value: number | undefined) {
    const dateRange = form.getFieldValue('dateRange');
    if (!isDateRangeValue(dateRange) || isSingleDay) return;

    const [start, end] = dateRange;
    await fetchAndUpdateWorkdayInfo(start, end, value || 0, mode);
  }

  // 监听模式变化，更新提示
  function handleModeChange(newMode: 'total' | 'daily') {
    setMode(newMode);
    handleHoursChange(form.getFieldValue('hours'));
  }

  // 提交表单
  async function handleSubmit() {
    try {
      // 验证任务必须有执行者
      if (!assigneeId) {
        Message.warning('请先为任务分配执行者');
        return;
      }

      const values = await form.validate() as WorkHourFormValues;

      if (isMountedRef.current) {
        setLoading(true);
      }

      // 编辑模式
      if (editWorkHour) {
        const hours = values.hours;
        const updateInput: WorkHourUpdateInput = {};

        if (hoursType === 'planned') {
          updateInput.plannedHours = hours;
        } else {
          updateInput.actualHours = hours;
          updateInput.description = values.description;
        }

        await updateWorkHour(editWorkHour.id, updateInput);

        if (isMountedRef.current) {
          Message.success('工时更新成功');
          onSuccess();
          onClose();
        }
      } else {
        // 创建模式
        const [startDate, endDate] = values.dateRange;

        // 确保日期是Dayjs对象
        const startDayjs = dayjs.isDayjs(startDate) ? startDate : dayjs(startDate);
        const endDayjs = dayjs.isDayjs(endDate) ? endDate : dayjs(endDate);

        const input: WorkHourCreateInput = {
          taskId,
          userId: assigneeId, // 使用任务执行者ID
          startDate: startDayjs.format('YYYY-MM-DD'),
          endDate: endDayjs.format('YYYY-MM-DD'),
          hoursType,
          mode,
          hours: values.hours,
          description: values.description,
        };

        await batchCreateWorkHours(input);

        if (isMountedRef.current) {
          Message.success('工时记录成功');
          onSuccess();
          onClose();
        }
      }
    } catch (error: unknown) {
      if (isMountedRef.current) {
        Message.error(error instanceof Error ? error.message : '操作失败');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }

  // 取消
  function handleCancel() {
    onClose();
  }

  return (
    <Modal
      title={`${editWorkHour ? '编辑' : '录入'}${hoursType === 'planned' ? '计划' : '实际'}工时 - ${taskName}`}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      unmountOnExit
      style={{ width: 600 }}
    >
      <Form form={form} layout="vertical">
        {/* 工时执行者（只读显示） */}
        <Form.Item label="工时执行者">
          <Space>
            <Avatar>{assigneeName?.[0] || '?'}</Avatar>
            <Text>{assigneeName || '未分配'}</Text>
          </Space>
        </Form.Item>

        {/* 执行时间 */}
        <Form.Item
          label="执行时间"
          field="dateRange"
          rules={[{ required: true, message: '请选择执行时间' }]}
        >
          <RangePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            onChange={handleDateChange}
            disabled={!!editWorkHour} // 编辑模式下禁用日期选择
            getPopupContainer={(node) => node.parentElement || document.body}
          />
        </Form.Item>

        {/* 工时数 */}
        <Form.Item
          label={hoursType === 'planned' ? '计划工时数' : '实际工时数'}
          required
        >
          <Space style={{ width: '100%' }}>
            {!isSingleDay && (
              <Select
                value={mode}
                onChange={handleModeChange}
                style={{ width: 100 }}
                getPopupContainer={(node) => node.parentElement || document.body}
              >
                <Select.Option value="total">总计</Select.Option>
                <Select.Option value="daily">每天</Select.Option>
              </Select>
            )}
            <Form.Item
              field="hours"
              rules={[{ required: true, message: '请输入工时' }]}
              noStyle
            >
              <InputNumber
                min={0}
                max={isSingleDay || mode === 'daily' ? 24 : 999}
                precision={1}
                placeholder="输入工时"
                suffix="小时"
                style={{ flex: 1 }}
                onChange={handleHoursChange}
              />
            </Form.Item>
          </Space>
        </Form.Item>

        {/* 工作日提示（放在 Form.Item 外部） */}
        {workdayInfo && (
          <div
            style={{
              marginTop: -16,
              marginBottom: 16,
              fontSize: 12,
              color: workdayInfo.startsWith('⚠️') ? '#f53f3f' : '#86909c',
              fontWeight: workdayInfo.startsWith('⚠️') ? 'bold' : 'normal',
            }}
          >
            {workdayInfo}
          </div>
        )}

        {/* 工作进展（仅实际工时显示） */}
        {hoursType === 'actual' && (
          <Form.Item label="工作进展" field="description">
            <TextArea placeholder="可选，描述工作内容" rows={3} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
